import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useGoogleLogin } from '@react-oauth/google';
import { useMemo } from 'react';
import { Loading } from '@/core/components/Loading';
import { useAuth } from '@/core/hooks/useAuth';
import { detectInAppBrowser } from '@/core/utils/browser';

const RETURN_URL_KEY = 'login_return_url';
const CALLBACK_PATH = '/auth/callback';
const APPLE_CLIENT_ID = 'run.runport';
const GRADIENT = 'linear-gradient(135deg, #8a2be2, #FFD700)';

export function LoginButtons({ returnUrl = '/dashboard' }) {
  const { loading: authLoading } = useAuth();
  const inAppBrowserInfo = useMemo(() => detectInAppBrowser(), []);

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: window.location.origin + CALLBACK_PATH,
  });

  const handleGoogleLogin = () => {
    sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    googleLogin();
  };

  const handleAppleLogin = () => {
    sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    sessionStorage.setItem('apple_client_id', APPLE_CLIENT_ID);

    const appleCallbackUrl = `${window.location.origin}/auth/apple-callback`;
    const stateValue = `${crypto.randomUUID()}:${window.location.origin}`;
    sessionStorage.setItem('apple_auth_state', stateValue);

    const params = new URLSearchParams({
      client_id: APPLE_CLIENT_ID,
      redirect_uri: appleCallbackUrl,
      response_type: 'code id_token',
      response_mode: 'form_post',
      scope: 'email',
      state: stateValue,
    });

    window.location.href = `https://appleid.apple.com/auth/authorize?${params.toString()}`;
  };

  if (authLoading) {
    return <Loading />;
  }

  if (inAppBrowserInfo.isInAppBrowser) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            maxWidth: 320,
            textAlign: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            ブラウザを変更してください
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
            このブラウザではログインできません
            <br />
            SafariやChromeで開き直してください
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
      <Button
        variant="contained"
        size="large"
        fullWidth
        startIcon={<AppleIcon />}
        onClick={handleAppleLogin}
        sx={{
          background: GRADIENT,
          color: '#fff',
          height: 52,
          maxWidth: 300,
          fontWeight: 600,
          boxShadow: '0 4px 20px #8a2be240',
          '&:hover': {
            background: GRADIENT,
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 30px #8a2be250',
          },
        }}
      >
        Appleでログイン
      </Button>
      <Button
        variant="contained"
        size="large"
        fullWidth
        startIcon={<GoogleIcon />}
        onClick={handleGoogleLogin}
        sx={{
          background: GRADIENT,
          color: '#fff',
          height: 52,
          maxWidth: 300,
          fontWeight: 600,
          boxShadow: '0 4px 20px #8a2be240',
          '&:hover': {
            background: GRADIENT,
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 30px #8a2be250',
          },
        }}
      >
        Googleでログイン
      </Button>
    </Box>
  );
}
